import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { UserSettingsContext } from '../context/userSettings';
import { rgba, media, p3 } from '../styles/stylesTheme';
import { borderRadius } from '../styles/stylesUtils';
import { getCSSImageUrl } from '../utils/mediaUrl';
import apiSendFeedback from '../api/apiSendFeedback';

const SendFeedbackModal = ({ isOpen, onClose, triggerNotification }) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const textareaRef = useRef(null);
    const backdropRef = useRef(null);
    const previousBodyOverflowRef = useRef('');
    const { settings } = useContext(UserSettingsContext);

    useEffect(() => {
        if (isOpen && textareaRef.current) {
            const focusTimer = setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, 200);

            return () => clearTimeout(focusTimer);
        }
    }, [isOpen]);

    // Handle ESC key to close modal
    useEffect(() => {
        if (!isOpen) return undefined;

        const handleEscape = e => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        previousBodyOverflowRef.current = document.body.style.overflow;
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = previousBodyOverflowRef.current;
        };
    }, [isOpen, onClose]);

    const handleBackdropClick = e => {
        if (e.target === backdropRef.current) {
            onClose();
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!feedbackText.trim() || isSubmitting) return;

        setIsSubmitting(true);

        try {
            await apiSendFeedback({
                feedback: feedbackText.trim(),
                user_id: settings?.user_id,
                email: settings?.email,
                homeLanguage: settings?.homeLanguage || 'en',
                targetLanguage: settings?.targetLanguage || 'sv',
            });

            if (triggerNotification) {
                triggerNotification('Feedback sent successfully!', 'success');
            }

            setFeedbackText('');
            onClose();
        } catch (error) {
            console.error('Error sending feedback:', error);

            if (triggerNotification && error.response?.status === 429) {
                triggerNotification(
                    'Too many feedback emails. Please try again later.',
                    'error'
                );
            } else if (triggerNotification) {
                triggerNotification('Failed to send feedback. Please try again.', 'error');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <ModalBackdrop
                    ref={backdropRef}
                    onClick={handleBackdropClick}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { duration: 0.2, ease: 'easeInOut' },
                    }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                    <StyledModal
                        onClick={e => e.stopPropagation()}
                        initial={{
                            opacity: 0,
                            y: '-45%',
                            x: '-50%',
                        }}
                        animate={{ opacity: 1, y: '-50%', x: '-50%' }}
                        exit={{
                            opacity: 0,
                            y: '-45%',
                            x: '-50%',
                            transition: {
                                y: {
                                    type: 'tween',
                                    duration: 0.3,
                                    ease: 'easeIn',
                                },
                                opacity: {
                                    duration: 0.2,
                                },
                            },
                        }}
                        transition={{
                            y: {
                                type: 'spring',
                                bounce: 1,
                                stiffness: 300,
                                damping: 16,
                                duration: 0.3,
                            },
                            opacity: {
                                duration: 0.2,
                                ease: 'easeInOut',
                            },
                        }}
                    >
                        <Container>
                            <HeaderContainer>
                                <h2>Send feedback</h2>
                                <CloseButton onClick={onClose} />
                            </HeaderContainer>

                            <ContentContainer>
                                <FeedbackForm onSubmit={handleSubmit}>
                                    <FeedbackTextArea
                                        ref={textareaRef}
                                        value={feedbackText}
                                        onChange={e => setFeedbackText(e.target.value)}
                                        placeholder="Share your thoughts, suggestions, or report issues..."
                                        maxLength={2000}
                                    />
                                    <SubmitButton
                                        type="submit"
                                        disabled={isSubmitting || !feedbackText.trim()}
                                    >
                                        {isSubmitting ? '...' : 'Send'}
                                    </SubmitButton>
                                </FeedbackForm>
                            </ContentContainer>
                        </Container>
                    </StyledModal>
                </ModalBackdrop>
            )}
        </AnimatePresence>
    );
};

const ModalBackdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
`;

const StyledModal = styled(motion.div)`
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 100000;
    font-size: 1.05em;
    width: 40em;
    max-height: 90vh;
    max-width: 95vw;
    ${borderRadius(0.8)}
    overflow: hidden;
    color: ${props => rgba(props.theme, 'black', 1)};
    background: ${props => rgba(props.theme, 'white', 1)};
    box-shadow:
        0 20px 60px -12px ${props => rgba(props.theme, 'black', 0.25)},
        0 8px 24px -8px ${props => rgba(props.theme, 'black', 0.1)};
    pointer-events: auto;
    overflow-y: auto;

    ${media.mobile} {
        width: 92%;
        max-width: 92%;
        font-size: 1em;
        border-radius: 1em;
    }
`;

const Container = styled.div`
    width: 100%;
    position: relative;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    background: ${props => rgba(props.theme, 'black', 0.05)};

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    ${media.mobile} {
        font-size: 1.2em;
        max-height: 70vh;
    }
`;

const HeaderContainer = styled.div`
    background: ${props => rgba(props.theme, 'colorAccent', 0.8)};
    background: ${props => p3(props.theme, 'pcolorAccent', 0.8)};
    padding: 0.1em 1.4em 0.05em;
    text-align: center;
    border-top-left-radius: 1.25em;
    border-top-right-radius: 1.25em;
    position: relative;

    h2 {
        font-size: 1.3em;
        font-family: var(--bold);
        color: ${props => rgba(props.theme, 'white', 1)};
        position: relative;
    }
`;

const ContentContainer = styled.div`
    padding: 1.5em;
    display: flex;
    flex-direction: column;
`;

const FeedbackForm = styled.form`
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 1em;
`;

const FeedbackTextArea = styled.textarea`
    flex: 1;
    padding: 1em;
    border: 1px solid ${props => rgba(props.theme, 'black', 0.1)};
    ${borderRadius(0.3)}
    font-size: 1em;
    font-family: var(--regular);
    background: ${props => rgba(props.theme, 'white', 1)};
    color: ${props => rgba(props.theme, 'black', 1)};
    resize: none;
    min-height: 5em;

    &:focus {
        outline: none;
        border-color: ${props => rgba(props.theme, 'colorAccent', 0.5)};
    }

    &::placeholder {
        color: ${props => rgba(props.theme, 'black', 0.4)};
    }
`;

const SubmitButton = styled.button`
    padding: 1em 2em;
    background: ${props => rgba(props.theme, 'colorAccent', 0.8)};
    background: ${props => p3(props.theme, 'pcolorAccent', 0.8)};
    color: ${props => rgba(props.theme, 'white', 1)};
    border: none;
    ${borderRadius(0.3)}
    cursor: pointer;
    transition: 150ms;
    font-size: 1em;
    font-family: var(--bold);
    align-self: flex-end;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 50%;
    right: 1.2em;
    transform: translateY(-50%);
    width: 2em;
    height: 2em;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    display: none;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 1.2em;
        height: 1.2em;
        background: ${props => rgba(props.theme, 'white', 1)};
        mask-image: url('${getCSSImageUrl("img/icon-x.svg")}');
        -webkit-mask-image: url('${getCSSImageUrl("img/icon-x.svg")}');
        mask-position: center;
        -webkit-mask-position: center;
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-size: contain;
        -webkit-mask-size: contain;
    }

    ${media.tablet} {
        display: block;
    }

    ${media.mobile} {
        right: 1em;
    }
`;

export default SendFeedbackModal;
